import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Fuse from "fuse.js";
import classnames from "classnames";

import useDebounce from "../../../../../hooks/useDebounce";
import useFuzzySearch from "../../../../../hooks/useFuzzySearch";
import useTranslations from "../../../../../hooks/useTranslations";
import useOnClickOutside from "../../../../../hooks/useOnClickOutside";

import { useWombatTheme } from "../../../theme";
import { FieldError } from "react-hook-form";
import { ChevronDownIcon } from "../../../../icons";
import { Checkbox } from "../Checkbox";
import Badge from "../../Badge";
import { TailSpin } from "../../Loader/TailSpin";
import { ReactHookFormProps } from ".";

export type SelectMultipleSearchableProps<T> = {
  /**
   * Label
   */
  label: string;
  /**
   * Define if the label shoulb only be seen on screen reader
   */
  srOnlyLabel?: boolean;
  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;
  /**
   * Select id, the component will automatically infer all data-test-id from it.
   *
   * data-test-id strategy:
   *  - Container: `select_${id}_group`
   *  - Label: `select_${id}_label`
   *  - Searchable input: `search_input_${id}`
   *
   *    *The sole purpose of this input is to display the selected item
   *   It is render as an input to allow for native interactions with React Hook Form*
   *  - Selected item input: `select_item_input_${id}`
   *
   *  - Dropdown container: `dropdown_container_${id}`
   *
   *   *Each items will be attributed a data-test-id based on their index*
   *  - Items: `select_${id}_item_${value}`
   *  - Items checkbox: `select_${id}_item_${value}_checkbox`
   *
   * It will also do the matching from the label to the input in respect to accessibility patterns
   */
  id: string;
  /**
   * Options of the select
   */
  items: T[];
  /**
   * Function that give back the value from an item T, used internally to retrieve the current value
   */
  getValue: (item: T) => string | number;
  /**
   * This function is triggered each time a change to the value is made
   * @default undefined
   */
  onChange?: (item: T[]) => void;
  /**
   * Loading state, used when the page can be shown even tho data for select is not already loaded
   * @default false
   */
  loading?: boolean;
  /**
   * Default value of the select
   *
   * If the given props is a `T`, the component will call `getValue` on this item
   */
  defaultValue?: T[];
  /**
   * Placeholder
   */
  placeholder?: string;
  /**
   * The react-hook-form error field
   * @default {}
   */
  error?: FieldError;
  /**
   * Set to true to allow multiple value selection
   */
  multiple: true;
  /**
   * Set to true to have a Fuzzy search on you're select
   * NOTE:
   * - As of now, accessibility pattern are not good with this sort of select so it might evolve in a near future
   * - The function signature of `renderItem` change to accept `Fuse.IFuseOptions<T>` in place of `T`
   */
  searchable: true;
  // /**
  //  * Allow the user to select all options at one
  //  * @default false
  //  */
  // canSelectAll?: boolean;
  /**
   * Only available with searchable set to true
   * NOTE: matchIfEmpty props is forced to be true
   */
  searchOption: Fuse.IFuseOptions<T>;
  /**
   * This function is called to render the checkbox label inside select dropdown
   */
  renderItem: (item: T) => string;
  /**
   * Used to render an empty search
   * @default t('wombat.select.emptyResult')
   */
  emptyResult?: () => React.ReactNode;
  /**
   * This is the prefered way of dealing with a select using react-hook-form.
   * It make use of the pattern described [here](https://react-hook-form.com/advanced-usage/#ConditionalControlledComponent)
   * Follow the pattern described in ReactHookFormProps to pass props to your component
   */
  RHF?: ReactHookFormProps;
};

/**
 * FIXME: Accessibility patterns are not perfect here
 * What should be done:
 *   - Give back focus to the input when an element is selected after a search
 *   - Give focus to input when a click is made on badges
 *   - Select with SPACE
 *   - Navigate through the options with arrows
 *   - The actual behaviour with focus on checkbox is crappy need some research on how to handle that
 */

/**
 * ENHANCEMENT: Allow `Badge` to be dismissable
 */

/**
 * A Select component that accept mutliple value as the answer
 * The component internally store the value inside checkboxes allowing for basic validation through react-hook-form
 */
const SelectMultipleSearchable = <T extends unknown>({
  label,
  id,
  srOnlyLabel,
  disabled = false,
  items,
  loading = false,
  // canSelectAll = false,
  placeholder,
  defaultValue,
  getValue,
  onChange,
  error,
  renderItem,
  searchOption,
  emptyResult,
  RHF,
}: SelectMultipleSearchableProps<T>) => {
  const t = useTranslations();
  const { theme } = useWombatTheme();

  const labelStyle = classnames(
    theme.form.select.label.base,
    disabled ? theme.form.select.label.disabled : theme.form.select.label.able,
    srOnlyLabel && "sr-only"
  );
  const selectStyle = classnames(
    theme.form.select.base,
    theme.form.select.status[error?.message ? "error" : "default"],
    "relative pointer-event-auto"
  );
  const itemLiStyle = classnames(
    theme.form.select.item.base,
    "pointer-event-auto"
  );
  const _emptyResult = useMemo(
    () => (emptyResult ? emptyResult : () => t("wombat.select.emptyResult")),
    [emptyResult]
  );

  const _defaultValue = useMemo((): T[] | null => {
    if (!defaultValue) return [];
    return defaultValue;
  }, [defaultValue]);

  useEffect(() => setSelectedItems([]), [items]);

  useEffect(() => {
    if (RHF) {
      RHF.register(RHF.name, RHF.registerOptions);
      return () => RHF.unregister(RHF.name);
    }
  }, [RHF?.register, RHF?.unregister, RHF?.name]);

  const [dropOpen, setDropOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const handleClickOutside = useCallback(() => {
    setDropOpen(false);
  }, []);

  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearchInputFocus = useCallback(() => {
    if (searchInputRef.current) searchInputRef.current.focus();
  }, [searchInputRef]);
  // const handleCaretClick = useCallback(() => {
  //   if (searchInputRef.current && !dropOpen) searchInputRef.current.focus();
  //   else setDropOpen(false);
  // }, [searchInputRef, dropOpen]);

  useOnClickOutside(popoverRef, handleClickOutside);

  const [value, setValue] = useState("");
  const [_selectedItems, setSelectedItems] = useState<T[]>([]);
  const debouncedValue = useDebounce(value, 200);

  /**
   * Reset selected items in case items change
   */
  useEffect(() => {
    setSelectedItems(_defaultValue);
    if (RHF)
      RHF.setValue(
        RHF.name,
        _defaultValue ? _defaultValue.map((_dv) => getValue(_dv)) : undefined,
        RHF.setValueOptions
      );
  }, [items]);

  const results = useFuzzySearch<T>(items, debouncedValue, {
    ...searchOption,
    matchIfEmpty: true,
  });

  const getItemIndex = useCallback(
    (item: T, items: T[]) =>
      items.findIndex((i) => getValue(i) === getValue(item)),
    [getValue]
  );

  const handleItemSelected = useCallback((item: Fuse.FuseResult<T>) => {
    setValue("");
    setSelectedItems((sItems) => {
      const itemIndex = getItemIndex(item.item, sItems);
      if (itemIndex === -1) {
        const newItems = [...sItems, item.item];
        if (onChange && typeof onChange === "function") onChange(newItems);
        if (RHF)
          RHF.setValue(
            RHF.name,
            newItems.map((i) => getValue(i)),
            RHF.setValueOptions
          );
        return newItems;
      }
      const newItemsWithDeletion = sItems.filter((_, i) => i !== itemIndex);

      if (onChange) onChange(newItemsWithDeletion);
      if (RHF)
        RHF.setValue(
          RHF.name,
          newItemsWithDeletion.map((i) => getValue(i)),
          RHF.setValueOptions
        );

      return newItemsWithDeletion;
    });
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
    },
    []
  );

  return (
    <div ref={popoverRef} className="flex-grow">
      <label
        className={labelStyle}
        htmlFor={id}
        data-test-id={`select_${id}_label`}
      >
        {label}
      </label>
      <div
        className={selectStyle}
        onClick={handleSearchInputFocus}
        data-test-id={`select_${id}_group`}
      >
        {_selectedItems.map((sItem) => (
          <Badge
            scale="sm"
            className="pointer-events-none"
            key={`badge-${getValue(sItem)}`}
            removable
            id={`select_${id}_badge_${getValue(sItem)}`}
            onRemove={() => handleItemSelected({ item: sItem, refIndex: 0 })}
          >
            {renderItem(sItem)}
          </Badge>
        ))}

        <input
          className="flex-grow pr-6 outline-none appearance-none min-w-xxs focus:outline-none"
          onFocus={() => setDropOpen(true)}
          ref={searchInputRef}
          placeholder={_selectedItems.length === 0 ? placeholder : undefined}
          onChange={handleInputChange}
          data-test-id={`search_input_${id}`}
          value={value}
        />
        {/** Note should probably have precedence over the div onClick event to properly close when dropdown is open */}
        <span className="absolute flex items-center w-5 h-5 m-0 transform -translate-x-8 inset-x-full">
          <ChevronDownIcon className="text-primary-900 w-4 h-4" />
        </span>
      </div>
      <div className="relative">
        <div
          data-test-id={`dropdown_container_${id}`}
          className={`${
            dropOpen ? "block" : "hidden"
          } absolute inset-0 z-20 w-full mt-1 bg-white rounded-md shadow-lg max-h-56`}
        >
          <ul
            tabIndex={-1}
            role="listbox"
            className={`relative flex flex-col py-1 overflow-auto text-base bg-white rounded-md ring-1 ring-black ring-opacity-5 max-h-56 focus:outline-none sm:text-sm ${
              "" //false ? "mb-10" : ""
            }`}
          >
            {/* NOTE: Will probably need to be virtualized when huge data set is passed */}
            {!loading &&
              results.length > 0 &&
              results.map((item) => (
                <li
                  role="checkbox"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleItemSelected(item);
                  }}
                  className={itemLiStyle}
                  key={getValue(item.item)}
                  data-test-id={`select_${id}_item_${getValue(item.item)}`}
                >
                  <Checkbox
                    className="pointer-events-none"
                    data-test-id={`select_${id}_item_${getValue(
                      item.item
                    )}_checkbox`}
                    label={renderItem(item.item)}
                    id={`${getValue(item.item)}`}
                    onClick={() => handleItemSelected(item)}
                    checked={getItemIndex(item.item, _selectedItems) !== -1}
                  />
                </li>
              ))}
            {!loading && results.length === 0 && _emptyResult()}
            {loading && (
              <div className="flex flex-col items-center justify-center flex-grow w-full h-32 bg-white rounded-md shadow-md">
                <TailSpin className="w-6 h-6 text-primary-600" />
              </div>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SelectMultipleSearchable);
